import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import useGetSession from '../../hook/useGetSession';
import { useSystemContext } from '../SystemContext/SystemContext';

interface Session {
  sessionId: string;
  isExpired: boolean;
}

interface SessionContextType {
  session: Session | null;
  sessionLoading: boolean;
  showExpiredPopup: boolean;
  checkSessionExpiry: () => boolean;
}

const SessionContext = createContext<SessionContextType>({
  session: null,
  sessionLoading: true,
  showExpiredPopup: false,
  checkSessionExpiry: () => false,
});

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [canFetch, setCanFetch] = useState(false);
  const [showExpiredPopup, setShowExpiredPopup] = useState(false);
  const { seatInfo, stadiumInfo } = useSystemContext();

  useEffect(() => {
    setCanFetch(true);
  }, []);

  const { data: sessionData, error, isLoading } = useGetSession(seatInfo?.id || '', stadiumInfo?.id || '', canFetch);

  useEffect(() => {
    if (!seatInfo?.id || !stadiumInfo?.id) {
      setSession(null);
      sessionStorage.removeItem('sessionId');
      setSessionLoading(false);
      setShowExpiredPopup(true);
      return;
    }

    if (!isLoading && error && (error as any).status === 403) {
      setSession(null);
      sessionStorage.removeItem('sessionId');
      setSessionLoading(false);
      setShowExpiredPopup(true);
      return;
    }

    if (!sessionData) return;

    try {
      const nowJST = new Date(Date.now() + JST_OFFSET_MS);
      const expiresAt = new Date(sessionData.expiresAt);
      const isExpired = expiresAt.getTime() < nowJST.getTime();

      if (isExpired) {
        setSession(null);
        sessionStorage.removeItem('sessionId');
        setShowExpiredPopup(true);
      } else {
        setSession({
          sessionId: sessionData.sessionId,
          isExpired: false,
        });
        setShowExpiredPopup(false);

        sessionStorage.setItem('sessionId', sessionData.sessionId);
      }
    } catch (error) {
      console.error('Session check error:', error);
      setSession(null);
      sessionStorage.removeItem('sessionId');
      setShowExpiredPopup(true);
    } finally {
      setSessionLoading(false);
    }
  }, [sessionData, seatInfo?.id, stadiumInfo?.id, error, isLoading]);

  const checkSessionExpiry = (): boolean => {
    if (!sessionData) return false;
    const nowJST = new Date(Date.now() + JST_OFFSET_MS);
    const expiresDate = new Date(sessionData?.expiresAt);
    const isExpired = expiresDate.getTime() < nowJST.getTime();

    if (isExpired) {
      setShowExpiredPopup(true);
      return true;
    }
    setShowExpiredPopup(false);

    return false;
  };

  return <SessionContext.Provider value={{ session, sessionLoading, showExpiredPopup, checkSessionExpiry }}>{children}</SessionContext.Provider>;
};

export const useSession = () => useContext(SessionContext);
